import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { productsApi } from '@/services/products';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export const AdminProductsPage = () => {
  const { data, isLoading } = useProducts();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = async (productId: string) => {
    setDeletingId(productId);
    setConfirmDeleteId(null);
    try {
      await productsApi.delete(productId);
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="admin-page-header" style={{ marginBottom: 'var(--space-6)' }}>
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Products
          </h1>
          <p
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            Manage your catalog of ready-made products.
          </p>
        </div>
        <Link to="/admin/products/new" className="responsive-button" style={{ textDecoration: 'none' }}>
          <Button className="responsive-button" style={{ width: '100%' }}>Add Product</Button>
        </Link>
      </div>

      {isLoading && (
        <div style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-20)' }}>
          <LoadingSpinner size="lg" />
        </div>
      )}

      {data && data.data.length === 0 && (
        <div
          className="text-center"
          style={{
            paddingTop: 'var(--space-12)',
            paddingBottom: 'var(--space-12)',
            color: 'var(--text-tertiary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
          }}
        >
          No products yet. Add your first product to get started.
        </div>
      )}

      {data && data.data.length > 0 && (
        <>
          {/* Mobile card view */}
          <div className="mobile-card-list">
            {data.data.map((product) => (
              <div
                key={product.productId}
                style={{
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-default)',
                  backgroundColor: 'var(--bg-surface)',
                  padding: 'var(--space-3) var(--space-4)',
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-2)' }}>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {product.name}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    ${product.basePrice.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                      borderRadius: 'var(--radius-full)',
                      backgroundColor: 'var(--bg-surface-sunken)',
                      padding: '2px 8px',
                    }}
                  >
                    {product.category}
                  </span>
                  <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                    <Link to={`/admin/products/${product.productId}/edit`}>
                      <Button variant="ghost" size="sm">Edit</Button>
                    </Link>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={deletingId === product.productId}
                      onClick={() => setConfirmDeleteId(product.productId)}
                    >
                      {deletingId === product.productId ? '...' : 'Remove'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div
            className="overflow-hidden desktop-table"
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
            }}
          >
            <div className="table-scroll">
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '480px' }}>
              <thead style={{ backgroundColor: 'var(--bg-surface-sunken)' }}>
                <tr>
                  {['Name', 'Category', 'Price', 'Actions'].map((heading, i) => (
                    <th
                      key={heading}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        textAlign: i === 3 ? 'right' : 'left',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        textTransform: 'uppercase',
                        letterSpacing: 'var(--tracking-wide)',
                        color: 'var(--text-tertiary)',
                        borderBottom: '1px solid var(--border-default)',
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((product) => (
                  <tr
                    key={product.productId}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      transition: 'background-color var(--duration-fast) var(--ease-out)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-surface-sunken)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <td
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {product.name}
                    </td>
                    <td
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      {product.category}
                    </td>
                    <td
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      ${product.basePrice.toFixed(2)}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }}>
                      <div className="flex items-center justify-end" style={{ gap: 'var(--space-2)' }}>
                        <Link to={`/admin/products/${product.productId}/edit`}>
                          <Button variant="ghost" size="sm">Edit</Button>
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={deletingId === product.productId}
                          onClick={() => setConfirmDeleteId(product.productId)}
                        >
                          {deletingId === product.productId ? '...' : 'Remove'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title="Remove product"
          message="Are you sure you want to remove this product? This action cannot be undone."
          confirmLabel="Remove"
          cancelLabel="Keep"
          onConfirm={() => handleDelete(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)}
          isPending={deletingId === confirmDeleteId}
        />
      )}
    </div>
  );
};
